import { type AuthEvent, type AuthEventType, AuthEventTypes } from './types';

const AUTH_CHANNEL_NAME = 'auth';
const CUSTOM_UNAUTHORIZED_EVENT = 'auth:unauthorized';

let authChannel: BroadcastChannel | null = null;

const getAuthChannel = (): BroadcastChannel | null => {
  if (typeof BroadcastChannel === 'undefined') {
    return null;
  }

  authChannel ??= new BroadcastChannel(AUTH_CHANNEL_NAME);

  return authChannel;
};

const createAuthEvent = (type: AuthEventType): AuthEvent => ({
  type,
  createdAt: Date.now(),
});

const broadcastAuthEvent = (type: AuthEventType): void => {
  getAuthChannel()?.postMessage(createAuthEvent(type));
};

/**
 * Вызывается после успешного входа.
 * Текущая вкладка уже обновила свой user-store, событие предназначено для остальных вкладок.
 */
export const broadcastSignedIn = (): void => {
  broadcastAuthEvent(AuthEventTypes.SignedIn);
};

/**
 * Вызывается после успешного выхода.
 * Текущая вкладка уже очистила свой user-store.
 */
export const broadcastSignedOut = (): void => {
  broadcastAuthEvent(AuthEventTypes.SignedOut);
};

// 401 должен обрабатываться и в текущей вкладке, и во всех соседних вкладках.
export const notifyUnauthorized = (): void => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent<AuthEvent>(CUSTOM_UNAUTHORIZED_EVENT, {
        detail: createAuthEvent(AuthEventTypes.Unauthorized),
      }),
    );
  }

  broadcastAuthEvent(AuthEventTypes.Unauthorized);
};

export const subscribeAuthEvents = (
  listener: (event: AuthEvent) => void,
): (() => void) => {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const channel = getAuthChannel();

  const handleBroadcastEvent = (event: MessageEvent<AuthEvent>): void => {
    listener(event.data);
  };

  const handleLocalUnauthorized = (event: Event): void => {
    listener((event as CustomEvent<AuthEvent>).detail);
  };

  channel?.addEventListener('message', handleBroadcastEvent);

  window.addEventListener(CUSTOM_UNAUTHORIZED_EVENT, handleLocalUnauthorized);

  return () => {
    channel?.removeEventListener('message', handleBroadcastEvent);

    window.removeEventListener(
      CUSTOM_UNAUTHORIZED_EVENT,
      handleLocalUnauthorized,
    );
  };
};
